/**
 * Sub-industry taxonomy for the guided template picker (plan §3.1 / §3.4).
 *
 * A sub-industry selects which ContentPack fills a composed goal template —
 * `key` values therefore match the content-pack module names under
 * `apps/cms/src/seeds/content-packs/{industry}/{key}.ts` exactly, and travel
 * over the wire as `subIndustry` from the picker → checkout → provisioning
 * saga → seed endpoint. Labels are the owner-facing wording used by picker
 * UIs and mirror `TemplateEntry.subIndustries`.
 */
import type { IndustryKey } from './template-goals';

export interface SubIndustryDef {
  /** Stable wire value; equals the ContentPack module name. */
  readonly key: string;
  /** Owner-facing label. */
  readonly label: string;
}

export const SUB_INDUSTRIES: Record<IndustryKey, readonly SubIndustryDef[]> = Object.freeze({
  beauty: [
    { key: 'hair', label: 'ヘアサロン' },
    { key: 'nail', label: 'ネイルサロン' },
    { key: 'eyelash', label: 'アイラッシュ' },
    { key: 'esthetic', label: 'エステティック' },
    { key: 'relaxation', label: 'リラクゼーション' },
    { key: 'private-salon', label: 'プライベートサロン' },
  ],
  restaurant: [
    { key: 'restaurant', label: 'レストラン' },
    { key: 'cafe', label: 'カフェ' },
    { key: 'izakaya', label: '居酒屋・バル' },
    { key: 'bakery', label: 'ベーカリー' },
    { key: 'ramen', label: 'ラーメン店' },
    { key: 'takeout', label: 'テイクアウト専門店' },
  ],
  law: [
    { key: 'law', label: '法律事務所' },
    { key: 'tax', label: '税理士・会計事務所' },
    { key: 'administrative', label: '行政書士' },
    { key: 'labor', label: '社会保険労務士' },
    { key: 'judicial', label: '司法書士' },
    { key: 'consulting', label: 'コンサルティング' },
  ],
  corporate: [
    { key: 'corporate', label: '総合' },
    { key: 'manufacturing', label: '製造業' },
    { key: 'it', label: 'IT・ソフトウェア' },
    { key: 'construction', label: '建設・工務店' },
    { key: 'recruiting', label: '採用強化' },
    { key: 'local-service', label: '地域密着サービス' },
  ],
  retail: [
    { key: 'retail', label: '総合' },
    { key: 'apparel', label: 'アパレル' },
    { key: 'flower', label: '花屋' },
    { key: 'grocery', label: '食料品' },
    { key: 'craft', label: 'クラフト・雑貨' },
    { key: 'showroom', label: 'ショールーム' },
  ],
  medical: [
    { key: 'medical', label: '一般内科・総合' },
    { key: 'dental', label: '歯科' },
    { key: 'pediatrics', label: '小児科' },
    { key: 'dermatology', label: '皮膚科' },
    { key: 'osteopathy', label: '整骨院・接骨院' },
    { key: 'ophthalmology', label: '眼科' },
  ],
});

export function getSubIndustries(industry: IndustryKey): readonly SubIndustryDef[] {
  return SUB_INDUSTRIES[industry];
}

export function isSubIndustryKey(industry: IndustryKey, key: string): boolean {
  return SUB_INDUSTRIES[industry].some((s) => s.key === key);
}
